import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import {
  TrendingUp, TrendingDown, BadgeDollarSign, Users2, AlertTriangle,
  X, Receipt, HeartPulse, Info,
} from 'lucide-react';

const fmtUsd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtUsdShort = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const SCOPES = [
  { id: 'live', label: 'Live' },
  { id: 'test', label: 'Test' },
  { id: 'comp', label: 'Comp' },
  { id: 'all', label: 'All' },
];

const CLASS_BADGE = {
  live: 'bg-[#E7F0E4] text-[#3F6B45]',
  test: 'bg-[#FBF1D9] text-[#8B6A3E]',
  comp: 'bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary)/0.7)]',
};

function KpiCard({ label, value, sub, tone = 'primary', testid, onClick }) {
  const tones = {
    primary: { bg: 'hsl(var(--card))', text: 'hsl(var(--primary))' },
    gold:    { bg: '#FBF1D9', text: '#8B6A3E' },
    green:   { bg: 'rgba(164,195,154,0.18)', text: '#3F6B45' },
    danger:  { bg: 'rgba(180,90,90,0.10)', text: '#7A3939' },
  };
  const t = tones[tone] || tones.primary;
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border border-[hsl(var(--primary)/0.15)] p-4 sm:p-5 text-left ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      style={{ backgroundColor: t.bg }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      <div className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.55)] font-semibold">
        {label}
      </div>
      <div className="font-serif text-3xl mt-1.5" style={{ color: t.text }}>{value}</div>
      {sub && <div className="text-[11px] text-[hsl(var(--primary)/0.6)] mt-1">{sub}</div>}
      {onClick && (
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[hsl(var(--accent))] mt-2">
          View transactions →
        </div>
      )}
    </div>
  );
}

function TrendBar({ data }) {
  const max = Math.max(1, ...data.map((d) => d.usd));
  return (
    <div className="flex items-end gap-1.5 h-32" data-testid="reports-monthly-bars">
      {data.map((d, i) => {
        const pct = Math.max(2, Math.round((d.usd / max) * 100));
        const isCurrent = i === data.length - 1;
        return (
          <div key={d.month} className="flex-1 flex flex-col items-center justify-end gap-1.5"
               title={`${d.month} — ${fmtUsd(d.usd)}`}>
            <div className="text-[9px] text-[hsl(var(--primary)/0.55)] tabular-nums">
              {d.usd ? fmtUsdShort(d.usd) : ''}
            </div>
            <div
              className="w-full rounded-t"
              style={{
                height: `${pct}%`,
                background: isCurrent
                  ? 'linear-gradient(180deg, #D9B775, #C5A059)'
                  : 'linear-gradient(180deg, #2A5A3E, #1A3626)',
              }}
            />
            <div className="text-[9px] text-[hsl(var(--primary)/0.6)] uppercase tracking-wider">
              {d.month.slice(5)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ChannelBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[11px] uppercase tracking-wider text-[hsl(var(--primary)/0.65)]">{label}</span>
        <span className="text-[13px] font-semibold text-[hsl(var(--primary))] tabular-nums">
          {fmtUsd(value)} · {pct}%
        </span>
      </div>
      <div className="h-2 rounded-full bg-[hsl(var(--primary)/0.08)] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function ModalShell({ title, subtitle, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: 'rgba(20,35,26,0.55)', backdropFilter: 'blur(4px)' }}
         onClick={onClose} data-testid={testid}>
      <div className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--primary)/0.15)] w-full max-w-5xl max-h-[85vh] flex flex-col shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-4 border-b border-[hsl(var(--primary)/0.1)] flex items-start justify-between gap-3 shrink-0">
          <div>
            <h3 className="font-serif text-xl text-[hsl(var(--primary))]">{title}</h3>
            {subtitle && <p className="text-[11px] text-[hsl(var(--primary)/0.6)] mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} data-testid="reports-modal-close"
                  className="p-1.5 rounded-lg hover:bg-[hsl(var(--primary)/0.06)] text-[hsl(var(--primary)/0.6)]">
            <X size={18} />
          </button>
        </header>
        <div className="overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

const METRIC_TITLES = {
  total: 'All revenue transactions',
  last30: 'Transactions · last 30 days',
  last90: 'Transactions · last 90 days',
  package: 'Package sales',
  plaque: 'Plaque & product orders',
  discounts: 'Discounted transactions',
  refunds: 'Refunded transactions',
};

function TransactionsModal({ metric, scope, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.get('/admin/reports/transactions', { params: { metric, scope } })
      .then(({ data: d }) => { if (!cancel) setData(d); })
      .catch((e) => toast.error(formatApiError(e, 'Could not load transactions.')))
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [metric, scope]);

  return (
    <ModalShell
      title={METRIC_TITLES[metric] || 'Transactions'}
      subtitle={data ? `${data.count} record${data.count === 1 ? '' : 's'} · reconciled total ${fmtUsd(data.total_usd)} · scope: ${scope}` : `Scope: ${scope}`}
      onClose={onClose}
      testid="reports-transactions-modal"
    >
      {loading ? (
        <div className="p-10 text-center text-[hsl(var(--primary)/0.55)]">Loading transactions…</div>
      ) : !data || data.rows.length === 0 ? (
        <div className="p-10 text-center text-[hsl(var(--primary)/0.55)]" data-testid="reports-transactions-empty">
          No transactions in this scope.
        </div>
      ) : (
        <table className="w-full text-[12px]" data-testid="reports-transactions-table">
          <thead className="sticky top-0 bg-[hsl(var(--primary))] text-[hsl(var(--accent))]">
            <tr>
              <th className="px-4 py-2 text-left text-[10px] tracking-[0.16em] uppercase">Customer</th>
              <th className="px-3 py-2 text-left text-[10px] tracking-[0.16em] uppercase">Memorial</th>
              <th className="px-3 py-2 text-left text-[10px] tracking-[0.16em] uppercase">Product</th>
              <th className="px-3 py-2 text-right text-[10px] tracking-[0.16em] uppercase">Gross</th>
              <th className="px-3 py-2 text-right text-[10px] tracking-[0.16em] uppercase">Discount</th>
              <th className="px-3 py-2 text-right text-[10px] tracking-[0.16em] uppercase">Net</th>
              <th className="px-3 py-2 text-left text-[10px] tracking-[0.16em] uppercase">Paid</th>
              <th className="px-3 py-2 text-left text-[10px] tracking-[0.16em] uppercase">Class</th>
              <th className="px-4 py-2 text-left text-[10px] tracking-[0.16em] uppercase">Stripe session</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={`${r.transaction_id}-${i}`} className="border-t border-[hsl(var(--primary)/0.08)]"
                  data-testid={`reports-txn-row-${i}`}>
                <td className="px-4 py-2">
                  <div className="text-[hsl(var(--primary))] font-medium truncate max-w-[160px]">{r.customer_name || '—'}</div>
                  <div className="text-[10px] text-[hsl(var(--primary)/0.55)] truncate max-w-[160px]">{r.customer_email || '—'}</div>
                </td>
                <td className="px-3 py-2 text-[hsl(var(--primary)/0.75)] truncate max-w-[130px]">{r.memorial_name || '—'}</td>
                <td className="px-3 py-2">
                  <span className="text-[hsl(var(--primary)/0.8)] capitalize">{(r.product || '—').replace(/_/g, ' ')}</span>
                  {r.kind === 'plaque' && (
                    <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FBF1D9] text-[#8B6A3E]">plaque</span>
                  )}
                  {r.promo_code && (
                    <div className="text-[10px] font-mono text-[#8B6A3E]">{r.promo_code}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-[hsl(var(--primary)/0.7)]">{fmtUsd(r.gross_usd)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-[#8B6A3E]">
                  {r.discount_usd > 0 ? `−${fmtUsd(r.discount_usd)}` : '—'}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-[hsl(var(--primary))]">
                  {fmtUsd(r.net_usd)}
                  {r.refunded_at && <div className="text-[9px] text-[#7A3939] uppercase">refunded</div>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-[hsl(var(--primary)/0.65)] tabular-nums">{fmtDate(r.paid_at)}</td>
                <td className="px-3 py-2">
                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold ${CLASS_BADGE[r.class] || CLASS_BADGE.comp}`}>
                    {r.class}
                  </span>
                </td>
                <td className="px-4 py-2 font-mono text-[10px] text-[hsl(var(--primary)/0.55)] truncate max-w-[150px]"
                    title={r.stripe_session_id || ''}>
                  {r.stripe_session_id || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ModalShell>
  );
}

function StuckPaymentsModal({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.get('/admin/reports/stuck-payments')
      .then(({ data: d }) => { if (!cancel) setData(d); })
      .catch((e) => toast.error(formatApiError(e, 'Could not load stuck payments.')))
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  return (
    <ModalShell
      title="Stuck payments"
      subtitle="Checkout sessions still pending in MyLeaf 15+ minutes after creation. They may be paid at Stripe but missed by a webhook."
      onClose={onClose}
      testid="reports-stuck-modal"
    >
      {loading ? (
        <div className="p-10 text-center text-[hsl(var(--primary)/0.55)]">Checking payment health…</div>
      ) : !data || data.rows.length === 0 ? (
        <div className="p-10 text-center text-[hsl(var(--primary)/0.55)]" data-testid="reports-stuck-empty">
          <HeartPulse size={20} className="inline mr-2 text-[#3F6B45]" />
          No stuck payments — every checkout session has resolved.
        </div>
      ) : (
        <ul className="divide-y divide-[hsl(var(--primary)/0.08)]">
          {data.rows.map((r, i) => (
            <li key={`${r.transaction_id}-${i}`} className="px-5 py-3" data-testid={`reports-stuck-row-${i}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-[hsl(var(--primary))]">
                    {r.customer_email || 'Unknown customer'}
                    <span className={`ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold ${CLASS_BADGE[r.class] || CLASS_BADGE.comp}`}>
                      {r.class}
                    </span>
                  </div>
                  <div className="text-[11px] text-[hsl(var(--primary)/0.6)]">
                    {r.memorial_name ? `${r.memorial_name} · ` : ''}{(r.plan_id || '—').replace(/_/g, ' ')} · {fmtUsd(r.amount_usd)}
                    {typeof r.age_hours === 'number' && ` · pending ${r.age_hours}h`}
                  </div>
                  <div className="text-[10px] font-mono text-[hsl(var(--primary)/0.5)] truncate">{r.session_id}</div>
                </div>
                <div className="text-[10px] text-[hsl(var(--primary)/0.55)] font-mono bg-[hsl(var(--primary)/0.04)] rounded px-2 py-1">
                  {r.repair_hint}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </ModalShell>
  );
}

export default function AdminReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('live');
  const [drillMetric, setDrillMetric] = useState(null);
  const [showStuck, setShowStuck] = useState(false);

  const load = useCallback((s) => {
    setLoading(true);
    api.get('/admin/reports/overview', { params: { scope: s } })
      .then(({ data: payload }) => setData(payload))
      .catch((e) => toast.error(formatApiError(e) || 'Failed to load reports'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(scope); }, [scope, load]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <AdminLayout>
          <div className="text-[hsl(var(--primary)/0.6)] p-12 text-center" data-testid="reports-loading">
            Loading report…
          </div>
        </AdminLayout>
      </div>
    );
  }
  if (!data) return null;

  const rev = data.revenue;
  const proj = data.projection;
  const mix = rev.channel_mix_usd || {};
  const totalChannelRev = (mix.direct || 0) + (mix.regional_partner || 0) + (mix.memorial_partner || 0);

  return (
    <div data-testid="admin-reports-page" className="min-h-screen bg-background">
      <Navbar />
      <AdminLayout>
        <header className="mb-5">
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">Platform admin</p>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
            <h1 className="font-serif text-4xl text-[hsl(var(--primary))]" data-testid="admin-reports-title">
              Reports &amp; insights
            </h1>
            {/* Live / Test / Comp / All scope toggle */}
            <div className="flex rounded-lg border border-[hsl(var(--primary)/0.18)] overflow-hidden"
                 data-testid="reports-scope-toggle">
              {SCOPES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setScope(s.id)}
                  data-testid={`reports-scope-${s.id}`}
                  className={`px-3.5 py-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold transition-colors ${
                    scope === s.id
                      ? 'bg-[hsl(var(--primary))] text-[hsl(var(--accent))]'
                      : 'bg-[hsl(var(--card))] text-[hsl(var(--primary)/0.6)] hover:bg-[hsl(var(--primary)/0.05)]'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-[hsl(var(--primary)/0.65)] text-sm mt-2">
            {scope === 'live'
              ? 'Showing live Stripe transactions only. Every figure reconciles to its transaction list.'
              : scope === 'test'
                ? 'Showing Stripe TEST-mode transactions — not real revenue.'
                : scope === 'comp'
                  ? 'Showing complimentary / manual activations — $0 or promotional.'
                  : 'Showing all paid records: live + test + complimentary.'}
          </p>
          <div className="flex items-start gap-1.5 text-[11px] text-[hsl(var(--primary)/0.55)] mt-2"
               data-testid="reports-disclaimer">
            <Info size={12} className="mt-0.5 shrink-0" />
            {data.disclaimer}
          </div>
        </header>

        {loading && (
          <div className="text-[11px] text-[hsl(var(--primary)/0.5)] mb-3">Refreshing…</div>
        )}

        {/* Stuck payments health card */}
        {data.stuck_payments_count > 0 && (
          <button
            type="button"
            onClick={() => setShowStuck(true)}
            data-testid="reports-stuck-card"
            className="w-full mb-5 rounded-xl border border-[#B45A5A]/40 bg-[rgba(180,90,90,0.08)] p-4 flex items-center gap-3 text-left hover:bg-[rgba(180,90,90,0.12)] transition-colors"
          >
            <AlertTriangle size={20} className="text-[#7A3939] shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-[#7A3939]">
                {data.stuck_payments_count} stuck payment{data.stuck_payments_count === 1 ? '' : 's'} detected
              </div>
              <div className="text-[12px] text-[#7A3939]/80">
                Checkout sessions pending 15+ minutes — possibly paid at Stripe but missed by a webhook. Click to review.
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#7A3939]">Review →</span>
          </button>
        )}
        {data.stuck_payments_count === 0 && (
          <div className="mb-5 rounded-xl border border-[hsl(var(--primary)/0.12)] bg-[rgba(164,195,154,0.12)] p-3 flex items-center gap-2 text-[12px] text-[#3F6B45]"
               data-testid="reports-payments-healthy">
            <HeartPulse size={15} />
            Payment health: all checkout sessions resolved — no stuck payments.
          </div>
        )}

        {/* KPI row — every card drills down to its transactions */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <KpiCard label="Gross revenue (all time)" value={fmtUsd(rev.gross_total_usd)}
                   sub={`${rev.paid_package_txns} package + ${rev.paid_plaque_orders} plaque sales`}
                   testid="kpi-total-revenue"
                   onClick={() => setDrillMetric('total')} />
          <KpiCard label="Last 30 days" value={fmtUsd(rev.gross_last_30_usd)}
                   sub={`Run rate ${fmtUsd(rev.daily_run_rate_usd)}/day`}
                   tone="green" testid="kpi-last-30"
                   onClick={() => setDrillMetric('last30')} />
          <KpiCard label="Package revenue" value={fmtUsd(rev.package_total_usd)}
                   sub="Memorial plans, all time"
                   testid="kpi-package-revenue"
                   onClick={() => setDrillMetric('package')} />
          <KpiCard label="Plaque & product revenue" value={fmtUsd(rev.plaque_total_usd)}
                   sub="Physical orders, all time"
                   testid="kpi-plaque-revenue"
                   onClick={() => setDrillMetric('plaque')} />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <KpiCard label="Projected next 30d" value={fmtUsd(proj.estimated_next_30_usd)}
                   sub={`Estimate: avg ${fmtUsd(proj.avg_daily_usd)}/day over last 90d × 30. Not scheduled renewals.`}
                   tone="gold" testid="kpi-projected-30" />
          <KpiCard label="Projected next 90d" value={fmtUsd(proj.estimated_next_90_usd)}
                   sub={`Estimate from ${proj.txns_used} sale${proj.txns_used === 1 ? '' : 's'} in the last 90 days.`}
                   tone="gold" testid="kpi-projected-90" />
          <KpiCard label="Discounts given" value={fmtUsd(rev.discounts_given_usd)}
                   sub="Promo codes applied"
                   testid="kpi-discounts"
                   onClick={() => setDrillMetric('discounts')} />
          <KpiCard label="Refunded" value={fmtUsd(rev.refunded_usd)}
                   sub="Returned to customers"
                   tone="danger" testid="kpi-refunds"
                   onClick={() => setDrillMetric('refunds')} />
        </div>

        {/* Monthly trend + channel mix */}
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-2 bg-[hsl(var(--card))] rounded-xl border border-[hsl(var(--primary)/0.15)] p-5"
               data-testid="reports-monthly-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl text-[hsl(var(--primary))]">Revenue · last 12 months</h2>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--primary)/0.5)]">
                Packages + plaques · paid only
              </span>
            </div>
            <TrendBar data={rev.monthly || []} />
          </div>

          <div className="bg-[hsl(var(--card))] rounded-xl border border-[hsl(var(--primary)/0.15)] p-5"
               data-testid="reports-channel-card">
            <h2 className="font-serif text-xl text-[hsl(var(--primary))] mb-4">Channel mix</h2>
            <div className="space-y-4">
              <ChannelBar label="Direct" value={mix.direct || 0} total={totalChannelRev} color="#1A3626" />
              <ChannelBar label="Via Regional Partner" value={mix.regional_partner || 0} total={totalChannelRev} color="#D9B775" />
              <ChannelBar label="Via Memorial Partner" value={mix.memorial_partner || 0} total={totalChannelRev} color="#A4C39A" />
            </div>
            <div className="text-[10px] text-[hsl(var(--primary)/0.5)] mt-4">
              Package revenue attributed via the customer's referral chain.
            </div>
          </div>
        </div>

        {/* Commission pipeline */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          {[
            { key: 'paid_usd', label: 'Commissions paid', color: '#3F6B45', testid: 'reports-commission-paid' },
            { key: 'pending_usd', label: 'Commissions pending', color: '#8B6A3E', testid: 'reports-commission-pending' },
            { key: 'reversed_usd', label: 'Commissions reversed', color: '#7A3939', testid: 'reports-commission-reversed' },
          ].map((c) => (
            <div key={c.key} className="rounded-xl border border-[hsl(var(--primary)/0.15)] bg-[hsl(var(--card))] p-4"
                 data-testid={c.testid}>
              <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
                <BadgeDollarSign size={16} style={{ color: c.color }} />
                <span className="text-[10px] uppercase tracking-[0.22em] font-semibold">{c.label}</span>
              </div>
              <div className="font-serif text-2xl mt-2 text-[hsl(var(--primary))]">
                {fmtUsd(data.commissions[c.key])}
              </div>
            </div>
          ))}
        </div>

        {/* Top partners by attributed revenue */}
        <section className="bg-[hsl(var(--card))] rounded-xl border border-[hsl(var(--primary)/0.15)] mb-6 overflow-hidden"
                 data-testid="reports-top-performers-card">
          <header className="px-5 py-3 bg-[hsl(var(--primary)/0.04)] border-b border-[hsl(var(--primary)/0.08)] flex items-center gap-2">
            <TrendingUp size={16} className="text-[#3F6B45]" />
            <h2 className="font-serif text-lg text-[hsl(var(--primary))]">Top partners by revenue</h2>
            <span className="ml-auto text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary)/0.55)]">
              Attributed sales · {scope} scope
            </span>
          </header>
          {(data.top_partners || []).length === 0 ? (
            <div className="p-6 text-center text-[hsl(var(--primary)/0.55)]" data-testid="reports-top-empty">
              No partner-attributed sales in this scope yet.
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="bg-[hsl(var(--primary))] text-[hsl(var(--accent))]">
                <tr>
                  <th className="px-4 py-2 text-left text-[10px] tracking-[0.18em] uppercase">#</th>
                  <th className="px-3 py-2 text-left text-[10px] tracking-[0.18em] uppercase">Partner</th>
                  <th className="px-3 py-2 text-left text-[10px] tracking-[0.18em] uppercase">Type</th>
                  <th className="px-3 py-2 text-right text-[10px] tracking-[0.18em] uppercase">Sales</th>
                  <th className="px-3 py-2 text-right text-[10px] tracking-[0.18em] uppercase">Revenue</th>
                  <th className="px-4 py-2 text-left text-[10px] tracking-[0.18em] uppercase">Last sale</th>
                </tr>
              </thead>
              <tbody>
                {data.top_partners.map((p, i) => (
                  <tr key={p.partner_id}
                      data-testid={`reports-top-row-${p.partner_id}`}
                      className="border-t border-[hsl(var(--primary)/0.08)]">
                    <td className="px-4 py-2.5 text-[hsl(var(--primary)/0.65)] tabular-nums">{i + 1}</td>
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-[hsl(var(--primary))] truncate max-w-[220px]">{p.name || '—'}</div>
                      <div className="text-[10px] text-[hsl(var(--primary)/0.55)] truncate max-w-[220px]">{p.email || '—'}</div>
                    </td>
                    <td className="px-3 py-2.5 capitalize text-[hsl(var(--primary)/0.75)] whitespace-nowrap">
                      {(p.account_type || '—').replace('_', ' ')}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{p.sales}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-[hsl(var(--primary))]">
                      {fmtUsd(p.revenue_usd)}
                    </td>
                    <td className="px-4 py-2.5 text-[hsl(var(--primary)/0.65)] whitespace-nowrap tabular-nums">
                      {fmtDate(p.last_sale_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Underperformers */}
        <section className="bg-[hsl(var(--card))] rounded-xl border border-[hsl(var(--primary)/0.15)] mb-6 overflow-hidden"
                 data-testid="reports-underperformers-card">
          <header className="px-5 py-3 bg-[#FBE9CC]/60 border-b border-[hsl(var(--primary)/0.08)] flex items-center gap-2">
            <AlertTriangle size={16} className="text-[#8B6A3E]" />
            <h2 className="font-serif text-lg text-[hsl(var(--primary))]">Regional Partners needing attention</h2>
            <span className="ml-auto text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary)/0.55)]">
              Zero attributed sales
            </span>
          </header>
          {(data.underperforming_partners || []).length === 0 ? (
            <div className="p-5 text-center text-[hsl(var(--primary)/0.55)]"
                 data-testid="reports-underperformers-empty">
              <TrendingDown size={18} className="inline mr-2 text-[#3F6B45]" />
              Every Regional Partner has at least one attributed sale. Nicely done.
            </div>
          ) : (
            <ul className="divide-y divide-[hsl(var(--primary)/0.08)]">
              {data.underperforming_partners.map((p) => (
                <li key={p.partner_id}
                    data-testid={`reports-underperformer-${p.partner_id}`}
                    className="px-5 py-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[hsl(var(--primary))] truncate">{p.name || '—'}</div>
                    <div className="text-[11px] text-[hsl(var(--primary)/0.55)] truncate">
                      {p.email || '—'} · Partner since {fmtDate(p.since)}
                    </div>
                  </div>
                  <Link
                    to={`/admin/partners/applications?search=${encodeURIComponent(p.name || '')}`}
                    className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[hsl(var(--accent))] hover:underline"
                  >
                    Open account →
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <SignedAgreementsPanel />

        <div className="text-[11px] text-[hsl(var(--primary)/0.45)] flex items-center gap-1 mt-6">
          <Users2 size={11} />
          Report generated {fmtDateTime(data.generated_at)} · scope: {scope}
        </div>

        {drillMetric && (
          <TransactionsModal metric={drillMetric} scope={scope} onClose={() => setDrillMetric(null)} />
        )}
        {showStuck && <StuckPaymentsModal onClose={() => setShowStuck(false)} />}
      </AdminLayout>
    </div>
  );
}

// ---------------------------------------------------------------------
// Monthly Signed Agreements audit panel (iter71)
// ---------------------------------------------------------------------
function SignedAgreementsPanel() {
  const today = new Date();
  const defaultMonth = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(defaultMonth);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = (m) => {
    setLoading(true);
    api.get('/admin/reports/signed-agreements', { params: { month: m } })
      .then(({ data }) => {
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setCount(Number.isFinite(data?.count) ? data.count : 0);
      })
      .catch((e) => {
        toast.error(formatApiError(e, 'Could not load signed agreements.'));
        setRows([]);
        setCount(0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(month); }, [month]);

  const downloadCsv = async () => {
    try {
      const res = await api.get('/admin/reports/signed-agreements.csv', {
        params: { month },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signed-agreements-${month}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 200);
      toast.success(`Downloaded signed-agreements-${month}.csv`);
    } catch (e) {
      toast.error(formatApiError(e, 'Could not download CSV.'));
    }
  };

  const apiOrigin = api.defaults.baseURL?.replace(/\/api$/, '') || '';

  return (
    <section
      className="bg-[hsl(var(--card))] border border-[hsl(var(--primary)/0.15)] rounded-2xl overflow-hidden"
      data-testid="reports-signed-agreements"
    >
      <header className="px-5 py-4 border-b border-[hsl(var(--primary)/0.1)] flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-serif text-xl text-[hsl(var(--primary))]">Signed Partner Agreements</h3>
          <p className="text-[11px] text-[hsl(var(--primary)/0.6)] mt-1">
            Every Regional &amp; Memorial Partner whose Independent Partner
            Agreement was approved + emailed during the selected month.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value || defaultMonth)}
            min="2024-01"
            max={defaultMonth}
            data-testid="reports-agreements-month-input"
            className="border border-[hsl(var(--primary)/0.18)] rounded-md px-3 py-1.5 text-[12px] bg-[hsl(var(--card))] text-[hsl(var(--primary))]"
          />
          <button
            type="button"
            onClick={downloadCsv}
            disabled={loading || count === 0}
            data-testid="reports-agreements-download-csv"
            className="ml-btn-primary !py-1.5 !px-3 !text-[10px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Download CSV
          </button>
        </div>
      </header>
      <div className="px-5 py-3 text-[11px] text-[hsl(var(--primary)/0.6)] tabular-nums">
        {loading
          ? 'Loading…'
          : count === 0
            ? `No agreements were approved in ${month}.`
            : `${count} agreement${count === 1 ? '' : 's'} approved in ${month}.`}
      </div>
      {!loading && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="reports-agreements-table">
            <thead>
              <tr className="text-left text-[hsl(var(--primary)/0.55)] text-[10px] tracking-[0.18em] uppercase font-semibold">
                <th className="px-5 py-2">Partner</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Approved</th>
                <th className="px-3 py-2">Agreement</th>
                <th className="px-5 py-2">Signed PDF</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.user_id}
                  data-testid={`reports-agreement-row-${r.user_id}`}
                  className="border-t border-[hsl(var(--primary)/0.08)] hover:bg-[hsl(var(--primary)/0.03)]"
                >
                  <td className="px-5 py-2.5">
                    <div className="text-[hsl(var(--primary))] font-medium">{r.name || '—'}</div>
                    <div className="text-[10px] text-[hsl(var(--primary)/0.55)]">{r.email || '—'}</div>
                  </td>
                  <td className="px-3 py-2.5 capitalize text-[hsl(var(--primary)/0.8)] whitespace-nowrap">
                    {(r.account_type || '').replace('_', ' ')}
                  </td>
                  <td className="px-3 py-2.5 text-[hsl(var(--primary)/0.7)] whitespace-nowrap tabular-nums">
                    {r.approved_at ? new Date(r.approved_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-[hsl(var(--primary)/0.7)] whitespace-nowrap">
                    {r.agreement_version || '—'}
                  </td>
                  <td className="px-5 py-2.5">
                    <a
                      href={`${apiOrigin}${r.signed_pdf_url}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`reports-agreement-pdf-${r.user_id}`}
                      className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[hsl(var(--accent))] hover:underline"
                    >
                      View PDF →
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
